/**
 * The popover every sentence slot opens.
 *
 * A `<body>` portal, not an absolutely positioned child — round 4 found the product-wide defect
 * this avoids: a popover nested inside an `overflow: hidden` ancestor is clipped to nothing
 * while still passing `toBeVisible()`, so the e2e tests hit-test with `elementFromPoint` and
 * the popover has to be a real top-layer element for that to pass. monday's own popovers are
 * `[data-testid="dialog"]` in a body portal too (`ui-reference/automations/INDEX.md`).
 */
import { createPortal } from 'react-dom';
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode, type RefObject } from 'react';

const GAP = 4;
const MARGIN = 8;

export interface SlotPopoverProps {
  /** The clicked placeholder; the card is anchored under it, flipping up when it must. */
  anchorRef: RefObject<HTMLElement | null>;
  onDismiss(): void;
  label: string;
  width?: number;
  children: ReactNode;
  testId?: string;
}

export function SlotPopover({ anchorRef, onDismiss, label, width, children, testId }: SlotPopoverProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);

  useEffect(() => {
    const onPointerDown = (event: MouseEvent) => {
      if (ref.current?.contains(event.target as Node)) return;
      if (anchorRef.current?.contains(event.target as Node)) return;
      onDismiss();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      onDismiss();
    };
    document.addEventListener('mousedown', onPointerDown, true);
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('mousedown', onPointerDown, true);
      document.removeEventListener('keydown', onKeyDown, true);
    };
  }, [anchorRef, onDismiss]);

  useLayoutEffect(() => {
    const place = () => {
      const anchor = anchorRef.current?.getBoundingClientRect();
      if (anchor === undefined) return;
      const box = ref.current?.getBoundingClientRect();
      const w = width ?? box?.width ?? 240;
      const h = box?.height ?? 0;
      let top = anchor.bottom + GAP;
      if (h > 0 && top + h > window.innerHeight - MARGIN) {
        top = anchor.top - GAP - h >= MARGIN ? anchor.top - GAP - h : Math.max(MARGIN, window.innerHeight - h - MARGIN);
      }
      const left = Math.max(MARGIN, Math.min(anchor.left, window.innerWidth - w - MARGIN));
      setPosition((current) => (current && current.top === top && current.left === left ? current : { top, left }));
    };
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [anchorRef, width, children]);

  return createPortal(
    <div
      ref={ref}
      className="ez-automation-popover"
      role="dialog"
      aria-label={label}
      data-testid={testId ?? 'automation-slot-popover'}
      style={{
        top: position?.top ?? -9999,
        left: position?.left ?? -9999,
        ...(width === undefined ? {} : { width }),
        visibility: position === null ? 'hidden' : 'visible',
      }}
    >
      {children}
    </div>,
    document.body,
  );
}

/** The grey caption every picker card opens with ("Select a column", "Who to notify?"). */
export function PopoverCaption({ children }: { children: ReactNode }) {
  return <div className="ez-automation-popover__caption">{children}</div>;
}

export function PopoverOption({
  children,
  onClick,
  selected,
  testId,
  chevron,
}: {
  children: ReactNode;
  onClick(): void;
  selected?: boolean;
  testId?: string;
  chevron?: boolean;
}) {
  return (
    <button
      type="button"
      className="ez-automation-popover__option"
      aria-selected={selected ?? false}
      role="option"
      data-testid={testId}
      onClick={onClick}
    >
      <span className="ez-automation-popover__option-label">{children}</span>
      {chevron === true ? <span className="ez-automation-popover__chevron" aria-hidden="true" /> : null}
    </button>
  );
}
